// =====================================================
// PAGE ADMIN: GESTION RENDEZ-VOUS PSY (VERSION PRO)
// =====================================================

import { useState, useEffect, useMemo } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { 
  Search, Clock, ArrowUpDown, Calendar, Eye, XCircle, CheckCircle, 
  AlertTriangle, Video, Phone, MessageSquare, MapPin, RefreshCw,
  ChevronLeft, ChevronRight, Download, Filter, User, Stethoscope,
  FileText, Ban, UserX, DollarSign, TrendingUp, CalendarDays, Loader2
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { formatAmountFCFA } from '../../../services/psyBookingService';
import { useToast } from '../../../components/ui/use-toast';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';

type SortField = 'date' | 'practitioner' | 'patient' | 'status' | 'amount' | 'payment';
type SortDirection = 'asc' | 'desc';
type DateFilter = 'all' | 'today' | 'week' | 'month';
type PaymentFilter = 'all' | 'paid' | 'pending' | 'refunded';

interface Appointment {
  id: string;
  practitioner_id: string;
  patient_id: string;
  scheduled_at: string;
  duration_minutes: number;
  timezone: string;
  appointment_type: string;
  consultation_mode: string;
  reason_for_visit: string | null;
  status: string;
  location_address: string | null;
  location_city: string | null;
  payment_status: string;
  amount_paid: number | null;
  amount_to_practitioner: number | null;
  commission_amount: number | null;
  transaction_reference: string | null;
  payment_method: string | null;
  cancelled_at: string | null;
  cancelled_by_role: string | null;
  cancellation_reason: string | null;
  refund_amount: number | null;
  patient_confirmed_at: string | null;
  practitioner_confirmed_at: string | null;
  reminder_sent_24h: boolean;
  reminder_sent_1h: boolean;
  admin_notes: string | null;
  created_at: string;
  practitioner: { id: string; full_name: string; email: string; phone?: string; photo_url?: string } | null;
  patient: { id: string; full_name: string; email: string; phone?: string } | null;
}

export default function AdminPsyAppointments() {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  
  // États principaux
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>('all');
  const [modeFilter, setModeFilter] = useState<string>('all');
  
  // Tri
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Modal détails
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Modal annulation
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  
  // Modal notes admin
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  
  // Modals de confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ type: string; id: string } | null>(null);

  useEffect(() => {
    loadAppointments();
  }, []);

  // Chargement avec filtrage multi-tenant
  const loadAppointments = async () => {
    try {
      setLoading(true);
      const { domain, tenantId } = getDomainFilterParams();
      
      let query = supabase
        .from('psy_appointments')
        .select(`
          *,
          practitioner:psy_practitioners(id, full_name, email, phone, photo_url),
          patient:psy_patients(id, full_name, email, phone)
        `);
      
      // Appliquer le filtrage par domaine
      query = applyDomainFilterToQuery(query, domain, tenantId);
      query = query.order('scheduled_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement des rendez-vous',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Statistiques calculées avec useMemo
  const stats = useMemo(() => {
    const total = appointments.length;
    const scheduled = appointments.filter((a) => a.status === 'scheduled').length;
    const confirmed = appointments.filter((a) => a.status === 'confirmed').length;
    const inProgress = appointments.filter((a) => a.status === 'in_progress').length;
    const completed = appointments.filter((a) => a.status === 'completed').length;
    const cancelled = appointments.filter((a) => a.status === 'cancelled').length;
    const noShow = appointments.filter((a) => a.status === 'no_show').length;
    
    const paidAppointments = appointments.filter((a) => a.payment_status === 'paid');
    const totalRevenue = paidAppointments.reduce((sum, a) => sum + (a.amount_paid || 0), 0);
    const totalCommission = paidAppointments.reduce((sum, a) => sum + (a.commission_amount || 0), 0);
    const totalToPractitioners = paidAppointments.reduce((sum, a) => sum + (a.amount_to_practitioner || 0), 0);
    
    const pendingPayment = appointments.filter((a) => a.payment_status === 'pending').length;
    const refunded = appointments.filter((a) => a.payment_status === 'refunded' || a.payment_status === 'partial_refund').length;
    
    const cancellationRate = total > 0 ? ((cancelled + noShow) / total * 100).toFixed(1) : '0';
    
    return {
      total, scheduled, confirmed, inProgress, completed, cancelled, noShow,
      totalRevenue, totalCommission, totalToPractitioners,
      pendingPayment, refunded, cancellationRate
    };
  }, [appointments]);

  // Tri
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc');
    }
  };

  // Filtrage avec useMemo
  const filteredAppointments = useMemo(() => {
    let filtered = [...appointments];

    // Recherche textuelle
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.practitioner?.full_name?.toLowerCase().includes(term) ||
          a.patient?.full_name?.toLowerCase().includes(term) ||
          a.practitioner?.email?.toLowerCase().includes(term) ||
          a.patient?.email?.toLowerCase().includes(term) ||
          a.transaction_reference?.toLowerCase().includes(term)
      );
    }

    // Filtre statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter((a) => a.status === statusFilter);
    }

    // Filtre date
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter((a) => {
        const appointmentDate = new Date(a.scheduled_at);
        switch (dateFilter) {
          case 'today':
            return appointmentDate >= today && appointmentDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
          case 'week':
            const weekStart = new Date(today.getTime() - today.getDay() * 24 * 60 * 60 * 1000);
            const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
            return appointmentDate >= weekStart && appointmentDate < weekEnd;
          case 'month':
            return appointmentDate.getMonth() === now.getMonth() && appointmentDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }

    // Filtre paiement
    if (paymentFilter !== 'all') {
      filtered = filtered.filter((a) => {
        if (paymentFilter === 'refunded') {
          return a.payment_status === 'refunded' || a.payment_status === 'partial_refund';
        }
        return a.payment_status === paymentFilter;
      });
    }

    // Filtre mode consultation
    if (modeFilter !== 'all') {
      filtered = filtered.filter((a) => a.consultation_mode === modeFilter);
    }

    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'date':
          comparison = new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
          break;
        case 'practitioner':
          comparison = (a.practitioner?.full_name || '').localeCompare(b.practitioner?.full_name || '');
          break;
        case 'patient':
          comparison = (a.patient?.full_name || '').localeCompare(b.patient?.full_name || '');
          break;
        case 'status':
          const statusOrder = { scheduled: 1, confirmed: 2, in_progress: 3, completed: 4, cancelled: 5, no_show: 6 };
          comparison = (statusOrder[a.status as keyof typeof statusOrder] || 7) - 
                       (statusOrder[b.status as keyof typeof statusOrder] || 7);
          break;
        case 'amount':
          comparison = (a.amount_paid || 0) - (b.amount_paid || 0);
          break;
        case 'payment':
          const paymentOrder = { paid: 1, pending: 2, refunded: 3, partial_refund: 4 };
          comparison = (paymentOrder[a.payment_status as keyof typeof paymentOrder] || 5) - 
                       (paymentOrder[b.payment_status as keyof typeof paymentOrder] || 5);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [appointments, searchTerm, statusFilter, dateFilter, paymentFilter, modeFilter, sortField, sortDirection]);

  // Pagination
  const paginatedAppointments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAppointments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAppointments, currentPage]);

  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);

  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, paymentFilter, modeFilter]);

  // Badges statut
  const getStatusBadge = (status: string) => {
    const badges: Record<string, { className: string; label: string; icon: React.ReactNode }> = {
      scheduled: { className: 'bg-blue-400/20 text-blue-400 border-blue-400/20', label: 'Planifié', icon: <Clock className="h-3 w-3 mr-1" /> },
      confirmed: { className: 'bg-green-400/20 text-green-400 border-green-400/20', label: 'Confirmé', icon: <CheckCircle className="h-3 w-3 mr-1" /> },
      in_progress: { className: 'bg-purple-400/20 text-purple-400 border-purple-400/20', label: 'En cours', icon: <Video className="h-3 w-3 mr-1" /> },
      completed: { className: 'bg-gray-400/20 text-gray-400 border-gray-400/20', label: 'Terminé', icon: <CheckCircle className="h-3 w-3 mr-1" /> },
      cancelled: { className: 'bg-red-400/20 text-red-400 border-red-400/20', label: 'Annulé', icon: <XCircle className="h-3 w-3 mr-1" /> },
      no_show: { className: 'bg-orange-400/20 text-orange-400 border-orange-400/20', label: 'Absent', icon: <UserX className="h-3 w-3 mr-1" /> },
    };
    const badge = badges[status] || badges.scheduled;
    return (
      <Badge className={`${badge.className} flex items-center`}>
        {badge.icon}{badge.label}
      </Badge>
    );
  };

  // Badge paiement
  const getPaymentBadge = (status: string) => {
    const badges: Record<string, { className: string; label: string }> = {
      paid: { className: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Payé' },
      pending: { className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'En attente' },
      refunded: { className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Remboursé' },
      partial_refund: { className: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Remb. partiel' },
    };
    const badge = badges[status] || badges.pending;
    return <Badge className={badge.className}>{badge.label}</Badge>;
  };

  // Icône mode consultation
  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'video': return <Video className="h-4 w-4 text-purple-400" />;
      case 'audio': return <Phone className="h-4 w-4 text-blue-400" />;
      case 'chat': return <MessageSquare className="h-4 w-4 text-green-400" />;
      case 'presentiel': return <MapPin className="h-4 w-4 text-orange-400" />;
      default: return <Video className="h-4 w-4 text-gray-400" />;
    }
  };

  const getModeLabel = (mode: string) => {
    const labels: Record<string, string> = {
      video: 'Vidéo', audio: 'Audio', chat: 'Chat', presentiel: 'Présentiel'
    };
    return labels[mode] || mode;
  };

  // Actions
  const handleViewDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setAdminNotes(appointment.admin_notes || '');
    setShowDetailsModal(true);
  };

  // Ouvrir modal de confirmation
  const openConfirmModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setPendingAction({ type: 'confirm', id: appointment.id });
    setShowConfirmModal(true);
  };

  const openCompleteModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setPendingAction({ type: 'complete', id: appointment.id });
    setShowCompleteModal(true);
  };

  const openNoShowModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setPendingAction({ type: 'noshow', id: appointment.id });
    setShowNoShowModal(true);
  };

  const openCancelModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Exécuter les actions
  const executeConfirmAppointment = async () => {
    if (!pendingAction) return;
    try {
      setActionLoading(pendingAction.id);
      const { error } = await supabase
        .from('psy_appointments')
        .update({ status: 'confirmed', updated_at: new Date().toISOString() })
        .eq('id', pendingAction.id);
      if (error) throw error;
      toast({ title: '✅ Succès', description: 'Le rendez-vous a été confirmé avec succès' });
      setShowConfirmModal(false);
      setShowDetailsModal(false);
      setPendingAction(null);
      await loadAppointments();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors de la confirmation', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const executeCancelAppointment = async () => {
    if (!selectedAppointment || !cancelReason.trim()) return;
    try {
      setActionLoading(selectedAppointment.id);
      const { error } = await supabase
        .from('psy_appointments')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by_role: 'admin',
          cancellation_reason: cancelReason,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedAppointment.id);
      if (error) throw error;
      toast({ title: '✅ Succès', description: 'Le rendez-vous a été annulé' });
      setShowCancelModal(false);
      setCancelReason('');
      setShowDetailsModal(false);
      await loadAppointments();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors de l\'annulation', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const executeMarkNoShow = async () => {
    if (!pendingAction) return;
    try {
      setActionLoading(pendingAction.id);
      const { error } = await supabase
        .from('psy_appointments')
        .update({ status: 'no_show', updated_at: new Date().toISOString() })
        .eq('id', pendingAction.id);
      if (error) throw error;
      toast({ title: '✅ Succès', description: 'Le patient a été marqué comme absent' });
      setShowNoShowModal(false);
      setShowDetailsModal(false);
      setPendingAction(null);
      await loadAppointments();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors de la mise à jour', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const executeMarkCompleted = async () => {
    if (!pendingAction) return;
    try {
      setActionLoading(pendingAction.id);
      const { error } = await supabase
        .from('psy_appointments')
        .update({ status: 'completed', updated_at: new Date().toISOString() })
        .eq('id', pendingAction.id);
      if (error) throw error;
      toast({ title: '✅ Succès', description: 'Le rendez-vous a été marqué comme terminé' });
      setShowCompleteModal(false);
      setShowDetailsModal(false);
      setPendingAction(null);
      await loadAppointments();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors de la mise à jour', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Fermer les modals de confirmation
  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
    setPendingAction(null);
  };

  const closeNoShowModal = () => {
    setShowNoShowModal(false);
    setPendingAction(null);
  };

  const handleSaveNotes = async () => {
    if (!selectedAppointment) return;
    try {
      setActionLoading(selectedAppointment.id);
      const { error } = await supabase
        .from('psy_appointments')
        .update({ admin_notes: adminNotes, updated_at: new Date().toISOString() })
        .eq('id', selectedAppointment.id);
      if (error) throw error;
      toast({ title: 'Succès', description: 'Notes enregistrées' });
      setShowNotesModal(false);
      loadAppointments();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors de l\'enregistrement', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = ['Date', 'Heure', 'Praticien', 'Patient', 'Mode', 'Statut', 'Paiement', 'Montant'];
    const rows = filteredAppointments.map(a => [
      new Date(a.scheduled_at).toLocaleDateString('fr-FR'),
      new Date(a.scheduled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      a.practitioner?.full_name || 'N/A',
      a.patient?.full_name || 'N/A',
      getModeLabel(a.consultation_mode),
      a.status,
      a.payment_status,
      a.amount_paid || 0
    ]);
    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rendez-vous-psy-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast({ title: 'Export réussi', description: 'Le fichier CSV a été téléchargé' });
  };

  // Format date/heure
  const formatDateTime = (dateString: string) => ({
    date: new Date(dateString).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
    time: new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1 flex items-center gap-3">
              <CalendarDays className="h-7 w-7 text-[#D4AF37]" />
              Gestion des Rendez-vous
            </h1>
            <p className="text-gray-400">
              {filteredAppointments.length} rendez-vous trouvé{filteredAppointments.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={loadAppointments}
              variant="outline"
              size="sm"
              className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            >
              <RefreshCw size={16} className="mr-2" />
              Actualiser
            </Button>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              size="sm"
              className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            >
              <Download size={16} className="mr-2" />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Stats enrichies */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">Total</div>
            <div className="text-xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">Planifiés</div>
            <div className="text-xl font-bold text-blue-400">{stats.scheduled}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">Confirmés</div>
            <div className="text-xl font-bold text-green-400">{stats.confirmed}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">Terminés</div>
            <div className="text-xl font-bold text-gray-400">{stats.completed}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">Annulés</div>
            <div className="text-xl font-bold text-red-400">{stats.cancelled}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">Absents</div>
            <div className="text-xl font-bold text-orange-400">{stats.noShow}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">Taux annul.</div>
            <div className="text-xl font-bold text-yellow-400">{stats.cancellationRate}%</div>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 backdrop-blur-sm border border-green-500/20 rounded-xl p-4">
            <div className="text-xs text-green-300 mb-1">Revenus</div>
            <div className="text-xl font-bold text-green-400">{formatAmountFCFA(stats.totalRevenue)}</div>
          </div>
        </div>

        {/* Revenus détaillés */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <div className="text-xs text-gray-400">Revenus totaux</div>
              <div className="text-lg font-bold text-white">{formatAmountFCFA(stats.totalRevenue)}</div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Stethoscope className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <div className="text-xs text-gray-400">Versé aux praticiens</div>
              <div className="text-lg font-bold text-white">{formatAmountFCFA(stats.totalToPractitioners)}</div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 flex items-center gap-4">
            <div className="p-3 bg-[#D4AF37]/20 rounded-lg">
              <TrendingUp className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-xs text-gray-400">Commissions</div>
              <div className="text-lg font-bold text-white">{formatAmountFCFA(stats.totalCommission)}</div>
            </div>
          </div>
        </div>

        {/* Filtres avancés */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-[#D4AF37]" />
            <span className="text-white font-medium">Filtres</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Recherche */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher praticien, patient, référence..."
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>
            {/* Statut */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
            >
              <option value="all" className="bg-gray-900">Tous les statuts</option>
              <option value="scheduled" className="bg-gray-900">Planifiés</option>
              <option value="confirmed" className="bg-gray-900">Confirmés</option>
              <option value="in_progress" className="bg-gray-900">En cours</option>
              <option value="completed" className="bg-gray-900">Terminés</option>
              <option value="cancelled" className="bg-gray-900">Annulés</option>
              <option value="no_show" className="bg-gray-900">Absents</option>
            </select>
            {/* Période */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as DateFilter)}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
            >
              <option value="all" className="bg-gray-900">Toutes les dates</option>
              <option value="today" className="bg-gray-900">Aujourd'hui</option>
              <option value="week" className="bg-gray-900">Cette semaine</option>
              <option value="month" className="bg-gray-900">Ce mois</option>
            </select>
            {/* Paiement */}
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value as PaymentFilter)}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
            >
              <option value="all" className="bg-gray-900">Tous les paiements</option>
              <option value="paid" className="bg-gray-900">Payés</option>
              <option value="pending" className="bg-gray-900">En attente</option>
              <option value="refunded" className="bg-gray-900">Remboursés</option>
            </select>
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    <button onClick={() => handleSort('date')} className="flex items-center gap-1 hover:text-white">
                      Date <ArrowUpDown size={12} className={sortField === 'date' ? 'text-[#D4AF37]' : ''} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    <button onClick={() => handleSort('practitioner')} className="flex items-center gap-1 hover:text-white">
                      Praticien <ArrowUpDown size={12} className={sortField === 'practitioner' ? 'text-[#D4AF37]' : ''} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    <button onClick={() => handleSort('patient')} className="flex items-center gap-1 hover:text-white">
                      Patient <ArrowUpDown size={12} className={sortField === 'patient' ? 'text-[#D4AF37]' : ''} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-white">
                      Statut <ArrowUpDown size={12} className={sortField === 'status' ? 'text-[#D4AF37]' : ''} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    <button onClick={() => handleSort('payment')} className="flex items-center gap-1 hover:text-white">
                      Paiement <ArrowUpDown size={12} className={sortField === 'payment' ? 'text-[#D4AF37]' : ''} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    <button onClick={() => handleSort('amount')} className="flex items-center gap-1 hover:text-white">
                      Montant <ArrowUpDown size={12} className={sortField === 'amount' ? 'text-[#D4AF37]' : ''} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {paginatedAppointments.map((appointment, index) => {
                  const dt = formatDateTime(appointment.scheduled_at);
                  return (
                    <tr key={appointment.id} className={`hover:bg-white/5 ${index % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'}`}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-white">{dt.date}</div>
                        <div className="text-xs text-gray-400">{dt.time} • {appointment.duration_minutes || 60}min</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <Stethoscope className="h-4 w-4 text-blue-400" />
                          </div>
                          <div>
                            <div className="text-sm text-white">{appointment.practitioner?.full_name || 'N/A'}</div>
                            <div className="text-xs text-gray-400">{appointment.practitioner?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                            <User className="h-4 w-4 text-[#D4AF37]" />
                          </div>
                          <div>
                            <div className="text-sm text-white">{appointment.patient?.full_name || 'N/A'}</div>
                            <div className="text-xs text-gray-400">{appointment.patient?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getModeIcon(appointment.consultation_mode)}
                          <span className="text-sm text-gray-300">{getModeLabel(appointment.consultation_mode)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(appointment.status)}</td>
                      <td className="px-4 py-3">{getPaymentBadge(appointment.payment_status)}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-white">{formatAmountFCFA(appointment.amount_paid || 0)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewDetails(appointment)} className="text-[#D4AF37] hover:text-[#D4AF37]/80 hover:bg-[#D4AF37]/10 p-1 h-8 w-8" title="Voir les détails">
                            <Eye size={16} />
                          </Button>
                          {appointment.status === 'scheduled' && (
                            <Button variant="ghost" size="sm" onClick={() => openConfirmModal(appointment)} disabled={actionLoading === appointment.id} className="text-green-400 hover:text-green-300 hover:bg-green-500/10 p-1 h-8 w-8" title="Confirmer le RDV">
                              {actionLoading === appointment.id ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                            </Button>
                          )}
                          {(appointment.status === 'confirmed' || appointment.status === 'in_progress') && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openCompleteModal(appointment)} disabled={actionLoading === appointment.id} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 p-1 h-8 w-8" title="Marquer comme terminé">
                                <CheckCircle size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openNoShowModal(appointment)} disabled={actionLoading === appointment.id} className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 p-1 h-8 w-8" title="Marquer comme absent">
                                <UserX size={16} />
                              </Button>
                            </>
                          )}
                          {appointment.status !== 'cancelled' && appointment.status !== 'completed' && appointment.status !== 'no_show' && (
                            <Button variant="ghost" size="sm" onClick={() => openCancelModal(appointment)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 h-8 w-8" title="Annuler le RDV">
                              <Ban size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {paginatedAppointments.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-400">
                      <Calendar className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                      <p>Aucun rendez-vous trouvé</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center p-4 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {Math.min((currentPage - 1) * itemsPerPage + 1, filteredAppointments.length)} - {Math.min(currentPage * itemsPerPage, filteredAppointments.length)} sur {filteredAppointments.length}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="bg-white/5 border-white/10 text-white">
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm text-white px-2">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="bg-white/5 border-white/10 text-white">
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Détails */}
      {showDetailsModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-2xl border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-3">
                <CalendarDays className="h-6 w-6 text-[#D4AF37]" />
                Détails du Rendez-vous
              </h2>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-white">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Date et statut */}
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2 text-white">
                  <Calendar className="h-5 w-5 text-[#D4AF37]" />
                  <span className="font-medium">{formatDateTime(selectedAppointment.scheduled_at).date}</span>
                  <span className="text-gray-400">à</span>
                  <span className="font-medium">{formatDateTime(selectedAppointment.scheduled_at).time}</span>
                </div>
                {getStatusBadge(selectedAppointment.status)}
                {getPaymentBadge(selectedAppointment.payment_status)}
              </div>

              {/* Praticien et Patient */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-sm text-gray-400 mb-3 flex items-center gap-2"><Stethoscope className="h-4 w-4" /> Praticien</h3>
                  <div className="text-white font-medium">{selectedAppointment.practitioner?.full_name || 'N/A'}</div>
                  <div className="text-sm text-gray-400">{selectedAppointment.practitioner?.email}</div>
                  {selectedAppointment.practitioner?.phone && <div className="text-sm text-gray-400">{selectedAppointment.practitioner.phone}</div>}
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-sm text-gray-400 mb-3 flex items-center gap-2"><User className="h-4 w-4" /> Patient</h3>
                  <div className="text-white font-medium">{selectedAppointment.patient?.full_name || 'N/A'}</div>
                  <div className="text-sm text-gray-400">{selectedAppointment.patient?.email}</div>
                  {selectedAppointment.patient?.phone && <div className="text-sm text-gray-400">{selectedAppointment.patient.phone}</div>}
                </div>
              </div>

              {/* Détails consultation */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Mode</div>
                  <div className="flex items-center gap-2 text-white">{getModeIcon(selectedAppointment.consultation_mode)} {getModeLabel(selectedAppointment.consultation_mode)}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Durée</div>
                  <div className="text-white">{selectedAppointment.duration_minutes || 60} min</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Montant</div>
                  <div className="text-white font-medium">{formatAmountFCFA(selectedAppointment.amount_paid || 0)}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Méthode</div>
                  <div className="text-white">{selectedAppointment.payment_method || 'N/A'}</div>
                </div>
              </div>

              {/* Raison visite */}
              {selectedAppointment.reason_for_visit && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-sm text-gray-400 mb-2">Raison de la consultation</h3>
                  <p className="text-white">{selectedAppointment.reason_for_visit}</p>
                </div>
              )}

              {/* Annulation */}
              {selectedAppointment.status === 'cancelled' && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                  <h3 className="text-sm text-red-400 mb-2 flex items-center gap-2"><XCircle className="h-4 w-4" /> Annulation</h3>
                  <p className="text-white">{selectedAppointment.cancellation_reason || 'Aucune raison fournie'}</p>
                  <p className="text-xs text-gray-400 mt-2">Par: {selectedAppointment.cancelled_by_role} • {selectedAppointment.cancelled_at && new Date(selectedAppointment.cancelled_at).toLocaleString('fr-FR')}</p>
                </div>
              )}

              {/* Notes admin */}
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm text-gray-400 flex items-center gap-2"><FileText className="h-4 w-4" /> Notes admin</h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowNotesModal(true)} className="text-[#D4AF37]">Modifier</Button>
                </div>
                <p className="text-white">{selectedAppointment.admin_notes || 'Aucune note'}</p>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-between">
              {/* Actions rapides */}
              <div className="flex gap-2">
                {selectedAppointment.status === 'scheduled' && (
                  <Button onClick={() => openConfirmModal(selectedAppointment)} className="bg-green-500 hover:bg-green-600 text-white">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirmer
                  </Button>
                )}
                {(selectedAppointment.status === 'confirmed' || selectedAppointment.status === 'in_progress') && (
                  <>
                    <Button onClick={() => openCompleteModal(selectedAppointment)} className="bg-blue-500 hover:bg-blue-600 text-white">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Terminer
                    </Button>
                    <Button onClick={() => openNoShowModal(selectedAppointment)} variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
                      <UserX className="h-4 w-4 mr-2" />
                      Absent
                    </Button>
                  </>
                )}
                {selectedAppointment.status !== 'cancelled' && selectedAppointment.status !== 'completed' && selectedAppointment.status !== 'no_show' && (
                  <Button onClick={() => openCancelModal(selectedAppointment)} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Ban className="h-4 w-4 mr-2" />
                    Annuler
                  </Button>
                )}
              </div>
              <Button variant="outline" onClick={() => setShowDetailsModal(false)} className="border-white/10 text-white hover:bg-white/5">
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation - Confirmer RDV */}
      {showConfirmModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-2xl border border-white/10 max-w-md w-full animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-400" />
                Confirmer le rendez-vous
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-300 mb-4">Voulez-vous confirmer ce rendez-vous ?</p>
              <div className="bg-white/5 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Patient</span>
                  <span className="text-white font-medium">{selectedAppointment.patient?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Praticien</span>
                  <span className="text-white font-medium">{selectedAppointment.practitioner?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white font-medium">{formatDateTime(selectedAppointment.scheduled_at).date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Heure</span>
                  <span className="text-white font-medium">{formatDateTime(selectedAppointment.scheduled_at).time}</span>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <Button variant="outline" onClick={closeConfirmModal} className="border-white/10 text-white hover:bg-white/5">
                Annuler
              </Button>
              <Button onClick={executeConfirmAppointment} disabled={actionLoading === selectedAppointment.id} className="bg-green-500 hover:bg-green-600 text-white">
                {actionLoading === selectedAppointment.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation - Terminer RDV */}
      {showCompleteModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-2xl border border-white/10 max-w-md w-full animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-blue-400" />
                Terminer le rendez-vous
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-300 mb-4">Voulez-vous marquer ce rendez-vous comme terminé ?</p>
              <div className="bg-white/5 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Patient</span>
                  <span className="text-white font-medium">{selectedAppointment.patient?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Praticien</span>
                  <span className="text-white font-medium">{selectedAppointment.practitioner?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white font-medium">{formatDateTime(selectedAppointment.scheduled_at).date}</span>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <Button variant="outline" onClick={closeCompleteModal} className="border-white/10 text-white hover:bg-white/5">
                Annuler
              </Button>
              <Button onClick={executeMarkCompleted} disabled={actionLoading === selectedAppointment.id} className="bg-blue-500 hover:bg-blue-600 text-white">
                {actionLoading === selectedAppointment.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Terminer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation - Absent (No-Show) */}
      {showNoShowModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-2xl border border-white/10 max-w-md w-full animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-3">
                <UserX className="h-6 w-6 text-orange-400" />
                Marquer comme absent
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-300 mb-4">Voulez-vous marquer ce patient comme absent (no-show) ?</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 mb-4">
                <p className="text-orange-300 text-sm">⚠️ Cette action indique que le patient ne s'est pas présenté au rendez-vous.</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Patient</span>
                  <span className="text-white font-medium">{selectedAppointment.patient?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Praticien</span>
                  <span className="text-white font-medium">{selectedAppointment.practitioner?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white font-medium">{formatDateTime(selectedAppointment.scheduled_at).date}</span>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <Button variant="outline" onClick={closeNoShowModal} className="border-white/10 text-white hover:bg-white/5">
                Annuler
              </Button>
              <Button onClick={executeMarkNoShow} disabled={actionLoading === selectedAppointment.id} className="bg-orange-500 hover:bg-orange-600 text-white">
                {actionLoading === selectedAppointment.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserX className="h-4 w-4 mr-2" />}
                Marquer absent
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Annulation */}
      {showCancelModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-2xl border border-white/10 max-w-md w-full animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-red-400" />
                Annuler le rendez-vous
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
                <p className="text-red-300 text-sm">⚠️ Cette action est irréversible. Le patient et le praticien seront notifiés.</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">Patient</span>
                  <span className="text-white font-medium">{selectedAppointment.patient?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Praticien</span>
                  <span className="text-white font-medium">{selectedAppointment.practitioner?.full_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white font-medium">{formatDateTime(selectedAppointment.scheduled_at).date} à {formatDateTime(selectedAppointment.scheduled_at).time}</span>
                </div>
              </div>
              <label className="block text-sm text-gray-400 mb-2">Raison de l'annulation *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Expliquez la raison de l'annulation..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 min-h-[100px] focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50"
              />
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowCancelModal(false); setCancelReason(''); }} className="border-white/10 text-white hover:bg-white/5">
                Retour
              </Button>
              <Button onClick={executeCancelAppointment} disabled={!cancelReason.trim() || actionLoading === selectedAppointment.id} className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-50">
                {actionLoading === selectedAppointment.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Notes */}
      {showNotesModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-2xl border border-white/10 max-w-md w-full">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-3">
                <FileText className="h-6 w-6 text-[#D4AF37]" />
                Notes administrateur
              </h2>
            </div>
            <div className="p-6">
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Notes internes..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 min-h-[150px]"
              />
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNotesModal(false)} className="border-white/10 text-white">Annuler</Button>
              <Button onClick={handleSaveNotes} disabled={actionLoading === selectedAppointment.id} className="bg-[#D4AF37] hover:bg-[#D4AF37]/80 text-black">
                {actionLoading === selectedAppointment.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
